// Spec 37's pure helpers: no Vue, like schedulingFormat.js. Everything a
// credential chip, a documents-on-file row or a holder link prints comes
// through here, so the credentials panel, the driver's checklist card and
// the Expiring page say the same thing about the same credential.
//
// WORDING RULE (spec 37 §1.10): this is a list of documents on file and
// their dates. Nothing here says "compliant", "compliance" or "DQ file
// complete": the product records paperwork, it does not certify a carrier,
// and a green chip that read as a compliance claim would be one.

/**
 * fleet.view is admin, dispatcher and accountant; fleet.manage is admin and
 * dispatcher (api/app/Services/UserAccessService.js). The store carries the
 * role, not the capability list, so the mapping is restated here the way
 * canManageScheduling restates scheduling.manage. Defensive like Team.vue's
 * isAdmin: no store (the unit tests) is nobody.
 */
function roleOf(store) {
  const getters = (store && store.getters) || {};
  if (getters.isAdmin) return 'admin';
  return (getters.user && getters.user.role) || null;
}

export function canViewFleet(store) {
  return ['admin', 'dispatcher', 'accountant'].includes(roleOf(store));
}

export function canManageFleet(store) {
  return ['admin', 'dispatcher'].includes(roleOf(store));
}

/**
 * The expiry chip for a credential row (CredentialService.shape's `state`
 * and `days_left`): { value, label } for LwChip's feedback axis.
 *   expired   danger   "Expired 2026-09-30"
 *   expiring  warning  "Expires in 12 days" / "Expires today"
 *   valid     success  "Valid until 2027-03-01"
 *   no_expiry neutral  "No expiry date"
 */
export function expiryChip(credential) {
  const c = credential || {};
  if (c.state === 'expired') return { value: 'danger', label: `Expired ${c.expires_on}` };
  if (c.state === 'expiring') {
    const days = Number(c.days_left);
    if (days === 0) return { value: 'warning', label: 'Expires today' };
    if (days === 1) return { value: 'warning', label: 'Expires in 1 day' };
    return { value: 'warning', label: `Expires in ${days} days` };
  }
  if (c.state === 'valid') return { value: 'success', label: `Valid until ${c.expires_on}` };
  return { value: 'neutral', label: 'No expiry date' };
}

/**
 * The line under a credential that stops an assignment. Only an ACTIVE
 * credential of a type in `fleet.block_on_expired` (the server's
 * `blocks_assignment`) ever does, and only once it has expired; an expiring
 * one is a warning about what is coming, said as such.
 */
export function blockHint(credential) {
  const c = credential || {};
  if (!c.blocks_assignment) return null;
  if (c.state === 'expired') return 'Blocks assignment';
  if (c.state === 'expiring') return 'Will block assignment once it expires';
  return null;
}

/** The status word for a row that is not plain `active`. */
export function statusChip(credential) {
  const c = credential || {};
  if (c.archived_at) return { value: 'neutral', label: 'Archived' };
  if (c.status === 'pending_review') return { value: 'info', label: 'Waiting for review' };
  if (c.status === 'superseded') return { value: 'neutral', label: 'Replaced' };
  if (c.status === 'rejected') return { value: 'neutral', label: 'Not accepted' };
  return null;
}

/**
 * One row of the documents-on-file checklist (GET /fleet/drivers/:id/
 * documents): present / expiring / expired / missing / pending_review.
 */
export function documentStatusChip(status) {
  return (
    {
      present: { value: 'success', label: 'On file' },
      expiring: { value: 'warning', label: 'Expiring' },
      expired: { value: 'danger', label: 'Expired' },
      missing: { value: 'neutral', label: 'Not on file' },
      pending_review: { value: 'info', label: 'Waiting for review' },
    }[status] || { value: 'neutral', label: status || 'Unknown' }
  );
}

/** "Driver", "Truck", "Trailer", "Subhauler", "Company". */
export function holderKindLabel(holderType) {
  return (
    {
      driver: 'Driver',
      truck: 'Truck',
      trailer: 'Trailer',
      subhauler: 'Subhauler',
      org: 'Company',
    }[holderType] || holderType
  );
}

/** The page a credential's holder lives on, or null for the company's own. */
export function holderRoute(credential) {
  const c = credential || {};
  if (c.holder_id == null) return null;
  const id = String(c.holder_id);
  // A driver's credentials are on their page's Documents tab (2026-10-13).
  if (c.holder_type === 'driver') {
    return { name: 'DriversEdit', params: { id }, query: { tab: 'documents' } };
  }
  if (c.holder_type === 'truck' || c.holder_type === 'trailer') {
    return { name: 'TrucksView', params: { id } };
  }
  if (c.holder_type === 'subhauler') return { name: 'SubhaulersEdit', params: { id } };
  return null;
}

/** The URL a credential's stored document opens at (a new tab; the API redirects or streams). */
export function documentHref(credential) {
  return `/api/fleet/credentials/${encodeURIComponent(credential.id)}/document`;
}

// The office upload's limits (api/app/Middleware/FleetUpload.js): checked
// here first only so a 40 MB phone scan is refused before it crawls up a
// cellular link; the server reads the bytes and decides.
export const DOCUMENT_ACCEPT = 'application/pdf,image/png,image/jpeg';
export const DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

export function documentFileProblem(file) {
  if (!file) return 'Choose a file.';
  if (file.size > DOCUMENT_MAX_BYTES)
    return 'That file is over 10 MB. Scan it smaller and try again.';
  if (file.type && !DOCUMENT_ACCEPT.split(',').includes(file.type)) {
    return 'Use a PDF, PNG or JPEG.';
  }
  return null;
}

/** The sentence a failed document upload shows. */
export function uploadProblem(err) {
  const res = err && err.response;
  if (!res) return 'The document did not reach the server. Check your connection and try again.';
  if (res.status === 403)
    return 'Uploading a document needs a dispatcher or administrator account.';
  if (res.status === 413) return 'That file is over 10 MB.';
  const code = res.data && res.data.error_code;
  if (code === 'FLEET_FILE_TYPE_INVALID') return 'Use a PDF, PNG or JPEG.';
  if (code === 'FLEET_FILE_MISSING') return 'Choose a file.';
  return 'The document could not be uploaded.';
}

/** The sentence a refused review or archive shows. */
export function reviewProblem(err, fallback) {
  const res = err && err.response;
  if (!res) return 'The request did not reach the server. Check your connection and try again.';
  if (res.status === 403) return 'This needs a dispatcher or administrator account.';
  if (res.status === 404)
    return 'That credential is no longer here. Reload to see where it stands.';
  if (res.status === 409) return 'Someone already dealt with this one. The list has been reloaded.';
  return fallback;
}
